import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../../components/Button/Button';
import Input from '../../components/Input/Input';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import superAdminAuthService from '../../services/superAdminAuth.service';
import { SuperAdminInvitationPreview } from '../../types/superAdmin.types';
import './SuperAdminAuth.scss';

const SuperAdminSetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setPassword, isLoading, error, clearError } = useSuperAdminAuth();
  const [invitation, setInvitation] = useState<SuperAdminInvitationPreview | null>(null);
  const [loadingInvitation, setLoadingInvitation] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [formData, setFormData] = useState({
    password: '',
    confirm_password: '',
  });

  const [formErrors, setFormErrors] = useState({
    password: '',
    confirm_password: '',
  });

  useEffect(() => {
    void import('./superAdminPages.lazy');
    void import(/* webpackChunkName: "super-admin-catalog" */ './SuperAdminTemplates');
  }, []);

  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setPageError('Lien d’invitation invalide.');
        setLoadingInvitation(false);
        return;
      }

      try {
        const response = await superAdminAuthService.getInvitation(token);
        if (response.success && response.data?.invitation) {
          setInvitation(response.data.invitation);
        } else {
          setPageError(response.error?.message || 'Invitation introuvable.');
        }
      } catch (err: any) {
        setPageError(err.response?.data?.error?.message || 'Invitation introuvable.');
      } finally {
        setLoadingInvitation(false);
      }
    };

    loadInvitation();
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const nextErrors = {
      password: '',
      confirm_password: '',
    };

    if (!formData.password) {
      nextErrors.password = 'Le mot de passe est obligatoire';
    } else if (formData.password.length < 8) {
      nextErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!formData.confirm_password) {
      nextErrors.confirm_password = 'La confirmation est obligatoire';
    } else if (formData.password !== formData.confirm_password) {
      nextErrors.confirm_password = 'Les mots de passe ne correspondent pas';
    }

    setFormErrors(nextErrors);
    return !nextErrors.password && !nextErrors.confirm_password;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      await setPassword({
        token,
        password: formData.password,
        confirm_password: formData.confirm_password,
      });
      navigate('/super-admin/dashboard', { replace: true });
    } catch {
      // handled in context
    }
  };

  return (
    <div className="super-admin-auth">
      <div className="super-admin-auth__card">
        <p className="super-admin-auth__eyebrow">ZEEDCOM Control</p>
        <h1 className="super-admin-auth__title">Définir le mot de passe</h1>

        {loadingInvitation ? (
          <p className="super-admin-auth__subtitle">Vérification de l’invitation…</p>
        ) : pageError ? (
          <>
            <div className="super-admin-auth__alert super-admin-auth__alert--error">{pageError}</div>
            <div className="super-admin-auth__footer">
              <Link to="/super-admin/login">Retour à la connexion</Link>
            </div>
          </>
        ) : (
          <>
            <p className="super-admin-auth__subtitle">
              Invitation pour <strong>{invitation?.first_name} {invitation?.last_name}</strong> ({invitation?.email}).
            </p>

            {error && <div className="super-admin-auth__alert super-admin-auth__alert--error">{error}</div>}

            <form className="super-admin-auth__form" onSubmit={handleSubmit}>
              <Input
                label="Mot de passe"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={formErrors.password}
                placeholder="Choisir un mot de passe"
                required
                fullWidth
              />

              <Input
                label="Confirmer le mot de passe"
                type="password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                error={formErrors.confirm_password}
                placeholder="Confirmer le mot de passe"
                required
                fullWidth
              />

              <div className="super-admin-auth__actions">
                <Button type="submit" variant="primary" size="large" fullWidth loading={isLoading}>
                  Activer mon accès
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default SuperAdminSetPassword;
